import { SERVED_HOST_MESSAGE, SERVED_HOST_PATTERN } from "./served-hosts.schemas.js";
import { MAX_ROOT_SHELL_HOSTS_PER_ORG, ROOT_SHELL_HOST_MAX_LENGTH } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/root-shell-hosts.schemas.ts
/**
* Wire schemas for the CP read of an org's root-shell hosts, its custom domains:
*
*   GET /internal/root-shell-hosts?orgSlug=   the DP answer, behind the CP mTLS guard
*   GET /v1/internal/data-planes/root-shell-hosts?orgId=   provisioning-svc, one row per placement
*   GET /v1/admin/orgs/:id/root-shell-hosts   site-admin-api, the same body for the console
*
* One declaration serves every hop. Requests are strict. Responses follow this package's rule: an
* unknown key from a newer DP drops out, and every other key keeps its bound. Each string has its
* length bound before its pattern runs.
*/
/** The DP internal route. */
const ROOT_SHELL_HOSTS_ROUTE = "/internal/root-shell-hosts";
/** The provisioning-svc route that site-admin-api calls, under the `v1/internal/data-planes` controller. */
const ROOT_SHELL_HOSTS_RELAY_ROUTE = "/v1/internal/data-planes/root-shell-hosts";
/** The largest DP answer the relay reads, in bytes. Four hosts fit in far less. */
const ROOT_SHELL_HOSTS_ANSWER_MAX_BYTES = 4096;
/** The most host rows one org holds on a DP: the DP's own cap. */
const ROOT_SHELL_HOSTS_MAX = MAX_ROOT_SHELL_HOSTS_PER_ORG;
/** A host name on the wire, in the grammar of `refusalFor` and the served-hosts document. */
const RootShellHostNameSchema = z.string().max(ROOT_SHELL_HOST_MAX_LENGTH).regex(SERVED_HOST_PATTERN, { message: SERVED_HOST_MESSAGE });
/** The DP query: the org by slug, with the slug bounds of the served-hosts document. */
const RootShellHostsQuerySchema = z.strictObject({ orgSlug: z.string().min(2).max(63) });
/**
* One host row. `verifiedAt` stays null until the TXT proof matched. `bound` is true when the DP
* lists the host for this org in its served-hosts document: served, or with its certificate pending.
*/
const RootShellHostSchema = z.object({
	host: RootShellHostNameSchema,
	verifiedAt: z.iso.datetime().nullable(),
	assignedAt: z.iso.datetime(),
	bound: z.boolean()
});
/**
* The DP answer. `orgFound` is false when the DP holds no org with the slug, so a 404 means a DP
* from before this route. `canonicalHost` is the host of the org's token issuer.
*/
const RootShellHostsAnswerSchema = z.object({
	orgFound: z.boolean(),
	canonicalHost: RootShellHostNameSchema.nullable(),
	hosts: z.array(RootShellHostSchema).max(ROOT_SHELL_HOSTS_MAX)
});
/**
* The relay query: the org by its CP id. `z.guid()` takes any 8-4-4-4-12 hex id, the same set as
* site-admin-api's `ParseUUIDPipe`, so an id the CP route admits never fails here as a 400.
*/
const RootShellHostsRelayQuerySchema = z.strictObject({ orgId: z.guid() });
/**
* How one data plane answered. `ok`: the DP answered. `unsupported`: the DP answered 404, a DP
* from before this route. `unreachable`: no answer inside the deadline, or an answer the schema refused.
*/
const ROOT_SHELL_HOSTS_PLANE_STATUSES = [
	"ok",
	"unsupported",
	"unreachable"
];
/** The message a placement row earns when a status other than `ok` carries an org or a host. */
const ROOT_SHELL_HOSTS_OFF_OK_MESSAGE = "a placement off ok carries orgFound false, no canonicalHost and no host";
/** One placement row of the CP read: the DP answer and the plane that gave it. */
const RootShellHostsPlacementSchema = RootShellHostsAnswerSchema.extend({
	dataPlaneId: z.uuid(),
	dataPlaneName: z.string().max(ROOT_SHELL_HOST_MAX_LENGTH),
	status: z.enum(ROOT_SHELL_HOSTS_PLANE_STATUSES)
}).refine((row) => row.status === "ok" || !row.orgFound && row.canonicalHost === null && row.hosts.length === 0, { message: ROOT_SHELL_HOSTS_OFF_OK_MESSAGE });
/** The CP read body: one row per placement of the org, in placement order. */
const RootShellHostsByPlacementSchema = z.object({ placements: z.array(RootShellHostsPlacementSchema) });
//#endregion
export { ROOT_SHELL_HOSTS_ANSWER_MAX_BYTES, ROOT_SHELL_HOSTS_MAX, ROOT_SHELL_HOSTS_OFF_OK_MESSAGE, ROOT_SHELL_HOSTS_PLANE_STATUSES, ROOT_SHELL_HOSTS_RELAY_ROUTE, ROOT_SHELL_HOSTS_ROUTE, RootShellHostNameSchema, RootShellHostSchema, RootShellHostsAnswerSchema, RootShellHostsByPlacementSchema, RootShellHostsPlacementSchema, RootShellHostsQuerySchema, RootShellHostsRelayQuerySchema };

//# sourceMappingURL=root-shell-hosts.schemas.js.map